"use client";

import { useState, useEffect, useCallback, useMemo } from 'react';
import { dmsApi } from '@/lib/api';
import { useAuth } from '@/hooks/use-auth';
import { Users as UsersIcon, Loader2, UserPlus, Pencil, Trash2 } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface UserRow {
  _id: string;
  name: string;
  email: string;
  departmentId?: string | { _id: string; name: string };
  roleId?: string | { _id: string; name: string; permissions?: string[] };
  createdAt?: string;
  updatedAt?: string;
}

function formatDate(iso?: string) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}

function getDepartmentName(departmentId?: string | { _id: string; name: string }): string {
  if (!departmentId) return '—';
  return typeof departmentId === 'object' && departmentId?.name
    ? departmentId.name
    : typeof departmentId === 'string'
      ? departmentId
      : '—';
}

function getRoleName(roleId?: string | { _id: string; name: string }): string {
  if (!roleId) return '—';
  return typeof roleId === 'object' && roleId?.name
    ? roleId.name
    : typeof roleId === 'string'
      ? roleId
      : '—';
}

function getDepartmentId(departmentId?: string | { _id: string; name: string }): string {
  if (!departmentId) return '';
  return typeof departmentId === 'object' && departmentId?._id ? departmentId._id : typeof departmentId === 'string' ? departmentId : '';
}

function getRoleId(roleId?: string | { _id: string; name: string }): string {
  if (!roleId) return '';
  return typeof roleId === 'object' && roleId?._id ? roleId._id : typeof roleId === 'string' ? roleId : '';
}

interface DepartmentOption {
  _id: string;
  name: string;
}

interface RoleOption {
  _id: string;
  name: string;
  permissions?: string[];
}

export function UsersPage() {
  const { user: currentUser, hasPermission } = useAuth();
  const currentUserId = (currentUser as any)?.id ?? (currentUser as any)?._id ?? null;
  const rawMyDept = (currentUser as any)?.departmentId;
  const myDepartmentId =
    typeof rawMyDept === 'string'
      ? rawMyDept
      : rawMyDept && typeof rawMyDept === 'object' && rawMyDept._id
        ? String(rawMyDept._id)
        : '';

  const [users, setUsers] = useState<UserRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [departments, setDepartments] = useState<DepartmentOption[]>([]);
  const [roles, setRoles] = useState<RoleOption[]>([]);

  const visibleUsers = useMemo(() => {
    if (!myDepartmentId) return users;
    return users.filter((u) => getDepartmentId(u.departmentId) === myDepartmentId);
  }, [users, myDepartmentId]);

  const departmentOptions = useMemo(() => {
    if (!myDepartmentId) return departments;
    return departments.filter((d) => d._id === myDepartmentId);
  }, [departments, myDepartmentId]);

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createEmail, setCreateEmail] = useState('');
  const [createPassword, setCreatePassword] = useState('');
  const [createDepartmentId, setCreateDepartmentId] = useState('');
  const [createRoleId, setCreateRoleId] = useState('');
  const [createSubmitting, setCreateSubmitting] = useState(false);

  const [editUser, setEditUser] = useState<UserRow | null>(null);
  const [editName, setEditName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editPassword, setEditPassword] = useState('');
  const [editDepartmentId, setEditDepartmentId] = useState('');
  const [editRoleId, setEditRoleId] = useState('');
  const [editSubmitting, setEditSubmitting] = useState(false);

  const [userToDelete, setUserToDelete] = useState<UserRow | null>(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);

  const loadUsers = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await dmsApi.users.list();
      setUsers(Array.isArray(data) ? data : []);
    } catch (err: any) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: err?.error ?? err?.message ?? 'Failed to load users.',
      });
      setUsers([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  useEffect(() => {
    if (!isCreateOpen && !editUser) return;
    const load = async () => {
      try {
        const [depts, rolesList] = await Promise.all([
          dmsApi.departments.list(),
          dmsApi.roles.list(),
        ]);
        setDepartments(Array.isArray(depts) ? depts : []);
        setRoles(Array.isArray(rolesList) ? rolesList : []);
      } catch (err: any) {
        toast({
          variant: 'destructive',
          title: 'Error',
          description: err?.error ?? err?.message ?? 'Failed to load departments or roles.',
        });
      }
    };
    load();
  }, [isCreateOpen, editUser]);

  const openCreate = () => {
    setCreateName('');
    setCreateEmail('');
    setCreatePassword('');
    setCreateDepartmentId(myDepartmentId || '');
    setCreateRoleId('');
    setIsCreateOpen(true);
  };

  const handleCreateUser = async () => {
    const name = createName.trim();
    const email = createEmail.trim();
    const password = createPassword;
    if (!name || !email || !password) {
      toast({ variant: 'destructive', title: 'Validation', description: 'Name, email and password are required.' });
      return;
    }
    setCreateSubmitting(true);
    try {
      await dmsApi.users.create({
        name,
        email,
        password,
        departmentId: createDepartmentId || '',
        roleId: createRoleId || '',
      });
      toast({ title: 'User created', description: `"${name}" was created successfully.` });
      setIsCreateOpen(false);
      loadUsers();
    } catch (err: any) {
      toast({
        variant: 'destructive',
        title: 'Create failed',
        description: err?.error ?? err?.message ?? 'Could not create user.',
      });
    } finally {
      setCreateSubmitting(false);
    }
  };

  const openEdit = (user: UserRow) => {
    setEditUser(user);
    setEditName(user.name ?? '');
    setEditEmail(user.email ?? '');
    setEditPassword('');
    setEditDepartmentId(getDepartmentId(user.departmentId));
    setEditRoleId(getRoleId(user.roleId));
  };

  const handleEditUser = async () => {
    if (!editUser) return;
    const name = editName.trim();
    const email = editEmail.trim();
    if (!name || !email) {
      toast({ variant: 'destructive', title: 'Validation', description: 'Name and email are required.' });
      return;
    }
    setEditSubmitting(true);
    try {
      await dmsApi.users.update(editUser._id, {
        name,
        email,
        password: editPassword || '',
        departmentId: editDepartmentId || '',
        roleId: editRoleId || '',
      });
      toast({ title: 'User updated', description: `"${name}" was updated successfully.` });
      setEditUser(null);
      loadUsers();
    } catch (err: any) {
      toast({
        variant: 'destructive',
        title: 'Update failed',
        description: err?.error ?? err?.message ?? 'Could not update user.',
      });
    } finally {
      setEditSubmitting(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;
    if (userToDelete._id === currentUserId) {
      toast({ variant: 'destructive', title: 'Not allowed', description: 'You cannot delete your own user.' });
      setUserToDelete(null);
      return;
    }
    setDeleteSubmitting(true);
    try {
      await dmsApi.users.delete(userToDelete._id);
      toast({ title: 'User deleted', description: `"${userToDelete.name}" was removed.` });
      setUserToDelete(null);
      loadUsers();
    } catch (err: any) {
      toast({
        variant: 'destructive',
        title: 'Delete failed',
        description: err?.error ?? err?.message ?? 'Could not delete user.',
      });
    } finally {
      setDeleteSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col h-full overflow-auto">
      <Card className="m-6 flex-1 min-h-0 flex flex-col">
        <CardHeader className="flex flex-row items-start justify-between space-y-0 gap-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <UsersIcon size={24} />
              Users
            </CardTitle>
            <CardDescription>
              {!myDepartmentId
                ? 'All users and their department and role.'
                : 'Users in your department only.'}
            </CardDescription>
          </div>
          {hasPermission('create_user') && (
            <Button onClick={openCreate} className="shrink-0">
              <UserPlus size={18} className="mr-2" />
              Create User
            </Button>
          )}
        </CardHeader>
        <CardContent className="flex-1 overflow-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : visibleUsers.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">
              {users.length > 0 && myDepartmentId
                ? 'No users in your department.'
                : 'No users found.'}
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Department</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Created</TableHead>
                  {(hasPermission('update_user') || hasPermission('delete_user')) && <TableHead className="w-[100px]">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {visibleUsers.map((user) => (
                  <TableRow key={user._id}>
                    <TableCell className="font-medium">{user.name ?? '—'}</TableCell>
                    <TableCell>{user.email ?? '—'}</TableCell>
                    <TableCell>{getDepartmentName(user.departmentId)}</TableCell>
                    <TableCell>{getRoleName(user.roleId)}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatDate(user.createdAt)}
                    </TableCell>
                    {(hasPermission('update_user') || hasPermission('delete_user')) && (
                      <TableCell className="flex items-center gap-1">
                        {hasPermission('update_user') && (
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(user)} title="Edit user">
                            <Pencil size={16} />
                          </Button>
                        )}
                        {hasPermission('delete_user') && user._id !== currentUserId && (
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" onClick={() => setUserToDelete(user)} title="Delete user">
                            <Trash2 size={16} />
                          </Button>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Create User</DialogTitle>
            <DialogDescription>
              Add a new user. Name, email and password are required.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="create-name">Name</Label>
              <Input
                id="create-name"
                value={createName}
                onChange={(e) => setCreateName(e.target.value)}
                placeholder="John Doe"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="create-email">Email</Label>
              <Input
                id="create-email"
                type="email"
                value={createEmail}
                onChange={(e) => setCreateEmail(e.target.value)}
                placeholder="john@example.com"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="create-password">Password</Label>
              <Input
                id="create-password"
                type="password"
                value={createPassword}
                onChange={(e) => setCreatePassword(e.target.value)}
                placeholder="secure-password"
                autoComplete="new-password"
              />
            </div>
            <div className="grid gap-2">
              <Label>Department</Label>
              <Select value={createDepartmentId || 'none'} onValueChange={(v) => setCreateDepartmentId(v === 'none' ? '' : v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select department" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— None —</SelectItem>
                  {departmentOptions.map((d) => (
                    <SelectItem key={d._id} value={d._id}>
                      {d.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label>Role</Label>
              <Select value={createRoleId || 'none'} onValueChange={(v) => setCreateRoleId(v === 'none' ? '' : v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— None —</SelectItem>
                  {roles.map((r) => (
                    <SelectItem key={r._id} value={r._id}>
                      {r.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setIsCreateOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateUser} disabled={createSubmitting}>
              {createSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating…
                </>
              ) : (
                'Create User'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editUser} onOpenChange={(open) => !open && setEditUser(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
            <DialogDescription>
              Update name, email, password (leave blank to keep current), department and role.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="edit-name">Name</Label>
              <Input
                id="edit-name"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                placeholder="John Doe"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="edit-email">Email</Label>
              <Input
                id="edit-email"
                type="email"
                value={editEmail}
                onChange={(e) => setEditEmail(e.target.value)}
                placeholder="john@example.com"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="edit-password">New password (optional)</Label>
              <Input
                id="edit-password"
                type="password"
                value={editPassword}
                onChange={(e) => setEditPassword(e.target.value)}
                placeholder="Leave blank to keep current"
                autoComplete="new-password"
              />
            </div>
            <div className="grid gap-2">
              <Label>Department</Label>
              <Select value={editDepartmentId || 'none'} onValueChange={(v) => setEditDepartmentId(v === 'none' ? '' : v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select department" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— None —</SelectItem>
                  {departmentOptions.map((d) => (
                    <SelectItem key={d._id} value={d._id}>
                      {d.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label>Role</Label>
              <Select value={editRoleId || 'none'} onValueChange={(v) => setEditRoleId(v === 'none' ? '' : v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— None —</SelectItem>
                  {roles.map((r) => (
                    <SelectItem key={r._id} value={r._id}>
                      {r.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditUser(null)}>
              Cancel
            </Button>
            <Button onClick={handleEditUser} disabled={editSubmitting}>
              {editSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving…
                </>
              ) : (
                'Save changes'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!userToDelete} onOpenChange={(open) => !open && setUserToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete user</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {userToDelete?.name ?? 'this user'}? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteSubmitting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); handleDeleteUser(); }}
              disabled={deleteSubmitting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
